import { useMemo, useCallback, useState, useEffect } from "react";
import classNames from "classnames";
import {
  AddStepRelation,
  BlueprintCanvasViewableStepLog,
  BlueprintIfElseStep,
  BlueprintRunnerStepLogEntryType,
  BlueprintStep,
  BlueprintStepType,
  StepCoverageLevel,
  StepLogIterationInfo,
  StepLoggingViewEnum,
} from "../../../models/Blueprints";
import { STEP_TYPES_TO_BE_DEPRECATED } from "../../../constants";
import {
  isStepTypeWithPaths,
  isStepTypeAPIRequest,
  ExpandStepsButton,
  isStepTypeFunctionalBP,
  calculateCoverageLevelForStep,
  isStepTypeIsScopeEnabled,
  getEffectivePulledStepTemplate,
} from "../../blueprint-editor/utils/BlueprintEditorUtils";
import { useParams, useHistory } from "react-router-dom";
import { navigateHelper, navigateToBlueprintEditor } from "../../../router/RouterUtils";

import useBlueprintContext from "../context/useBlueprintContext";
import AddStepArrow from "./AddStepArrow";
import isEqual from "lodash/isEqual";
import ContextMenu from "../../shared/ContextMenu";
import StepCard from "../../shared/StepCard";
import { showSuccessToast } from "../../shared/Toasts";
import StepNotePreview from "./StepNotePreview";
import { fetchCodeBlockGitHubUrl } from "../utils/BlueprintEditorAPIClient";
import useLatestBlueprintVersionID from "./hooks/useLatestBlueprintVersionID";
import {
  StepCardStatusBadgeProps,
  StepCardStatusBadgeTypeEnum,
} from "../../shared/stepcard/models/StepCardModels";
import StepCardStepLog from "../../shared/stepcard/StepCardStepLog";
import { ExternalLink } from "lucide-react";

type RouteParams = {
  integrationID: string;
};

export type additionalCardInfoContent = {
  value: string | string[];
};

interface Props {
  isHighlighted: boolean;
  isDescendantofSelectedStep: boolean;
  step: BlueprintStep;
  stepNote: string | null;
  stepLog?: BlueprintCanvasViewableStepLog;
  handleSelectStepLogFromIteration?: (
    stepID: string,
    selectedIterationInfo: StepLogIterationInfo
  ) => void;
}

const BlueprintStepCard = ({
  isHighlighted,
  isDescendantofSelectedStep,
  step,
  stepNote,
  stepLog,
  handleSelectStepLogFromIteration,
}: Props) => {
  const {
    blueprintRunnerExecutionResponse,
    stepTemplates,
    genericStepTemplates,
    setSelectedStep,
    setSelectedSteps,
    deleteStep,
    collapseSubsteps,
    setCopiedStep,
    setCopiedSteps,
    selectedSteps,
    deleteSteps,
    deleteIfElseStepAndMoveChildrenUp,
    stepLoggingView,
    isTracing,
    tracedStepIDs,
    codeBlockBranch,
  } = useBlueprintContext();
  const history = useHistory();

  const { integrationID } = useParams<RouteParams>();

  const isFunctionalBpStep = isStepTypeFunctionalBP(step);
  const isCodeBlockStep = step.template.step_type === BlueprintStepType.CodeBlock;
  const additionalCardInfo: Record<string, additionalCardInfoContent> = {};

  // CodeBlock-specific state for GitHub URL
  const [githubUrl, setGithubUrl] = useState<string | null>(null);
  const [isLoadingGithubUrl, setIsLoadingGithubUrl] = useState(false);

  // TODO: Paul S - Currently we store a blueprint ID, not a blueprint version ID
  // in our step json for functional BP steps. We need access to the version
  // ID in order to navigate to the blueprint, so we fetch it
  // Once we store the functional blueprint version ID in the step, we will not
  // need to fetch the version ID any more

  // https://app.asana.com/0/1204663397868682/1206049177181091/f
  const functionalBlueprintVersionId = useLatestBlueprintVersionID(
    step?.template?.metadata?.blueprint_id,
    isFunctionalBpStep && !!integrationID
  );

  // Fetch GitHub URL for CodeBlock steps
  useEffect(() => {
    if (isCodeBlockStep) {
      const codeBlockName = step?.template?.metadata?.code_block;
      const codeBlockVersion = step?.template?.metadata?.version;

      if (codeBlockName && codeBlockVersion) {
        setIsLoadingGithubUrl(true);
        fetchCodeBlockGitHubUrl({
          blockName: codeBlockName,
          versionName: codeBlockVersion,
          branch: codeBlockBranch,
          onSuccess: (data) => {
            setGithubUrl(data.github_url);
            setIsLoadingGithubUrl(false);
          },
          onError: () => {
            setGithubUrl(null);
            setIsLoadingGithubUrl(false);
          },
        });
      }
    }
  }, [isCodeBlockStep, step, codeBlockBranch]);

  const logsForStep =
    blueprintRunnerExecutionResponse?.exit_data?.step_logs?.filter(
      (log) => log.step_id === step.id
    ) ?? [];

  const stepHasWarning = logsForStep.some(
    (log) => log.entry_type === BlueprintRunnerStepLogEntryType.Warning
  );
  const stepHasError = logsForStep.some(
    (log) => log.entry_type === BlueprintRunnerStepLogEntryType.Error
  );

  const coverageLevel = useMemo(() => {
    return calculateCoverageLevelForStep(
      blueprintRunnerExecutionResponse?.exit_data?.coverage?.step_coverage?.[step.id],
      false
    ).coverageLevel;
  }, [blueprintRunnerExecutionResponse?.exit_data?.coverage?.step_coverage?.[step.id]]);

  const pulledMatchingTemplate = useMemo(() => {
    if ((!stepTemplates && !genericStepTemplates) || !step.template) {
      return null;
    }
    const stepTemplate = stepTemplates
      ? stepTemplates.find((template) => template.id === step.template.id)
      : undefined;
    const genericStepTemplate = genericStepTemplates
      ? genericStepTemplates.find((template) => template.id === step.template.id)
      : undefined;
    return stepTemplate || genericStepTemplate;
  }, [stepTemplates, genericStepTemplates, step.template]);

  const newStepTemplate = useMemo(() => {
    if (!pulledMatchingTemplate || !step.template) return null;
    const effectiveStepTemplate = getEffectivePulledStepTemplate(
      step.template,
      pulledMatchingTemplate
    );
    return !isEqual(effectiveStepTemplate, step.template) ? effectiveStepTemplate : null;
  }, [pulledMatchingTemplate, step.template]);

  const isMissingMatchingTemplate = useMemo(
    () => !!stepTemplates.length && !!genericStepTemplates.length && !pulledMatchingTemplate,
    [stepTemplates.length, pulledMatchingTemplate]
  );

  const willBeDeprecated = step.template
    ? STEP_TYPES_TO_BE_DEPRECATED.includes(step.template.step_type)
    : false;

  const shouldHighlightAsTracedStep = useMemo(() => isTracing && tracedStepIDs.includes(step.id), [
    isTracing,
    tracedStepIDs,
  ]);

  const shouldDisplayStepLogInCanvas =
    stepLoggingView === StepLoggingViewEnum.STEP_IO_IN_CANVAS && stepLog;

  const shouldDisplayStepCoverageInCanvas =
    stepLoggingView === StepLoggingViewEnum.STEP_COVERAGE && coverageLevel;

  const cardClass = classNames(
    !shouldDisplayStepCoverageInCanvas
      ? ""
      : coverageLevel === StepCoverageLevel.INCOMPLETE
      ? "bg-yellow-0 border border-yellow-50"
      : coverageLevel === StepCoverageLevel.MISSING
      ? "bg-red-0 border border-red-50"
      : "bg-teal-0 border border-teal-50",
    isHighlighted
      ? "shadow-[0px_0px_0px_2px] shadow-blue-40"
      : isDescendantofSelectedStep
      ? "shadow-[0px_0px_0px_2px] shadow-blue-10"
      : null,
    shouldHighlightAsTracedStep && "bg-purple-0",
    !shouldDisplayStepCoverageInCanvas && !shouldHighlightAsTracedStep && "bg-white"
  );

  const [isShowingContextMenu, setIsShowingContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({
    x: 0,
    y: 0,
  });

  const onRightClickStep = useCallback(
    (e) => {
      e.preventDefault();
      setIsShowingContextMenu(true);
      setContextMenuPosition({ x: e.pageX, y: e.pageY });
    },
    [step, setContextMenuPosition, setIsShowingContextMenu]
  );

  const getContextMenuOptions = () => {
    const isStepInSelection = selectedSteps && selectedSteps.includes(step);
    const selectedStepsLength = isStepInSelection ? selectedSteps.length : -1;
    let baseOptions = [
      {
        label: isStepInSelection ? "Copy " + selectedStepsLength + " steps" : "Copy step",
        featherIconName: "copy",
        onClick: () => {
          setIsShowingContextMenu(false);
          if (isStepInSelection) {
            setCopiedSteps(selectedSteps.slice());
            navigator.clipboard.writeText(JSON.stringify(selectedSteps.slice()));
            showSuccessToast(selectedStepsLength + " steps copied.");
          } else {
            setCopiedStep(step as BlueprintStep);
            navigator.clipboard.writeText(JSON.stringify(step));
            showSuccessToast("Step copied.");
          }
        },
      },
      {
        label: isStepInSelection ? "Cut " + selectedStepsLength + " steps" : "Cut step",
        featherIconName: "scissors",
        onClick: () => {
          setIsShowingContextMenu(false);
          if (isStepInSelection) {
            setCopiedSteps(selectedSteps.slice());
            deleteSteps(selectedSteps as BlueprintStep[]);
            showSuccessToast(selectedStepsLength + " steps cut.");
          } else {
            setCopiedStep(step as BlueprintStep);
            deleteStep(step.id);
            showSuccessToast("Step cut.");
          }
        },
      },
      {
        label: isStepInSelection ? "Delete " + selectedStepsLength + " steps" : "Delete step",
        featherIconName: "trash",
        onClick: () => {
          setIsShowingContextMenu(false);
          if (isStepInSelection) {
            deleteSteps(selectedSteps as BlueprintStep[]);
          } else {
            deleteStep(step.id);
          }
        },
        confirmationMessage: `Are you sure you want to delete steps ${
          isStepInSelection ? selectedSteps.map((step) => step.id).join(", ") : step.id
        }?`,
      },
    ];

    const stepHasCollapsableChildren = isStepTypeWithPaths(step);
    if (stepHasCollapsableChildren) {
      baseOptions.push({
        label: step.hasCollapsedSubsteps ? "Expand substeps" : "Collapse substeps",
        featherIconName: step.hasCollapsedSubsteps ? "arrow-right" : "arrow-left",
        onClick: () => {
          setIsShowingContextMenu(false);
          collapseSubsteps(step.id);
        },
      });
    }

    const isIfElseStep = step.template.step_type === BlueprintStepType.IfElse;
    if ((isIfElseStep && step.paths?.["true"]?.length) || step.paths?.["false"]?.length) {
      baseOptions.push({
        label: "Delete Step + Move Substeps Up",
        featherIconName: "trash",
        onClick: () => {
          setIsShowingContextMenu(false);
          deleteIfElseStepAndMoveChildrenUp(step as BlueprintIfElseStep);
        },
        confirmationMessage: `Are you sure you want to delete the If/Else step ${step.id}? This will move the substeps up a level, with steps in the 'true' branch coming before steps in the 'false' branch.`,
      });
    }

    const isAPIRequestStep = isStepTypeAPIRequest(step);
    if (isAPIRequestStep && integrationID && step.template?.endpoint) {
      baseOptions.push({
        label: "Navigate to API endpoint",
        featherIconName: "edit",
        onClick: () => {
          setIsShowingContextMenu(false);
          navigateHelper(
            history,
            `/integration-builder/${integrationID}/api-endpoints/${step.template?.endpoint}`,
            true
          );
        },
      });
    }
    if (isFunctionalBpStep && integrationID) {
      baseOptions.push({
        label: "Navigate to Functional Blueprint",
        featherIconName: "edit",
        onClick: () => {
          setIsShowingContextMenu(false);
          navigateToBlueprintEditor(history, integrationID, functionalBlueprintVersionId!, true);
        },
      });
    }
    baseOptions.push({
      label: "Copy link to step",
      featherIconName: "link",
      onClick: () => {
        setIsShowingContextMenu(false);
        const url =
          window.location.origin +
          window.location.pathname +
          "?step=" +
          encodeURIComponent(step.id);
        navigator.clipboard.writeText(url);
        showSuccessToast("Link to step copied.");
      },
    });
    return baseOptions;
  };

  // We want to display API Request error codes in the canvas for visibility.
  if (isStepTypeAPIRequest(step)) {
    additionalCardInfo.allowedErrorCodes = {
      value: step.template?.metadata?.allowed_error_codes ?? [],
    };
  }

  if (isStepTypeIsScopeEnabled(step)) {
    additionalCardInfo.commonModel = {
      value: [step.template?.metadata?.common_model.id] ?? [],
    };
  }
  const stepTemplateName = step.template?.metadata?.common_model?.name
    ? `${step.template.name} for ${step.template.metadata.common_model.name} Common Model`
    : step.template.name;

  const getStepCardStatusBadge = (): StepCardStatusBadgeProps | undefined => {
    let badgeType: StepCardStatusBadgeTypeEnum = StepCardStatusBadgeTypeEnum.WARNING;
    let tooltipTitle: string = "";
    let badgeTitle: string = "";
    if (isMissingMatchingTemplate) {
      tooltipTitle = "The template ID for this step cannot be found";
      badgeTitle = "Not found";
      badgeType = StepCardStatusBadgeTypeEnum.ERROR;
    } else if (willBeDeprecated) {
      tooltipTitle = "This step will be deprecated";
      badgeTitle = "Deprecated";
    } else if (newStepTemplate) {
      tooltipTitle = "This step has a new step template";
      badgeTitle = "Old version";
    } else if (stepHasWarning) {
      tooltipTitle = "This step ran into a warning. Check step logs.";
      badgeTitle = "Run warning";
    } else if (stepHasError) {
      tooltipTitle = "This step ran into an error. Check step logs.";
      badgeTitle = "Run error";
      badgeType = StepCardStatusBadgeTypeEnum.ERROR;
    } else {
      return undefined;
    }
    return { type: badgeType, tooltipTitle: tooltipTitle, badgeTitle: badgeTitle };
  };

  return (
    <div className="flex items-center">
      <div>
        <AddStepArrow step={step} relation={AddStepRelation.SIBLING_BEFORE} />
        <ContextMenu
          items={getContextMenuOptions()}
          isShown={isShowingContextMenu}
          position={contextMenuPosition}
          onClose={() => setIsShowingContextMenu(false)}
        />
        <div className="flex flex-col gap-y-1">
          <StepCard
            cardClassName={cardClass}
            title={stepTemplateName}
            stepType={step.template.step_type}
            subtitle={step.id}
            id={`${step.id}-${step.template.name}`}
            stepParameterValues={step.parameter_values}
            stepImg={step.template.image}
            onClick={() => {
              setSelectedStep(step);
              setSelectedSteps(undefined);
              setIsShowingContextMenu(false);
            }}
            onContextMenu={onRightClickStep}
            additionalCardInfo={additionalCardInfo}
            statusBadgeProps={getStepCardStatusBadge()}
          />
          {isCodeBlockStep && githubUrl && !isLoadingGithubUrl && (
            <a
              href={githubUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline ml-2"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink size={12} />
              View code on GitHub
            </a>
          )}
        </div>
        <AddStepArrow step={step} relation={AddStepRelation.SIBLING_AFTER} />
        {step.hasCollapsedSubsteps && (
          <ExpandStepsButton onClick={() => collapseSubsteps(step.id)} />
        )}
      </div>
      {shouldDisplayStepLogInCanvas ? (
        <StepCardStepLog
          stepLog={stepLog}
          handleSelectStepLogFromIteration={handleSelectStepLogFromIteration}
        />
      ) : (
        <StepNotePreview stepNoteText={stepNote} />
      )}
    </div>
  );
};

export default BlueprintStepCard;
